/** Backend API のラッパ。各画面はここを呼ぶ。 */
import { apiFetch, apiFetchBlob } from "./client";
import type {
  AllocationImportResponse,
  DashboardSummary,
  ErrorsResponse,
  FetchOrdersResponse,
  FulfillmentResponse,
  IssueLabelsResponse,
  LabelIssuanceJobDto,
  OrderDto,
  OrderListResponse,
  OrderExportImportResponse,
  PrintJobDto,
  RecipientNameActionResponse,
  ReissueEcmsLabelResponse,
  ResetStatusResponse,
} from "./types";

// ---------------- Orders ----------------

export function listOrders(params: {
  platform?: string;
  status?: string;
  country_code?: string;
  order_no?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<OrderListResponse> {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  });
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return apiFetch<OrderListResponse>(`/api/orders${suffix}`);
}

export function getOrder(id: string): Promise<OrderDto> {
  return apiFetch<OrderDto>(`/api/orders/${id}`);
}

export function fetchOrders(body: {
  since_hours?: number;
  platforms?: string[];
  parallel?: boolean;
} = {}): Promise<FetchOrdersResponse> {
  return apiFetch<FetchOrdersResponse>("/api/orders/fetch", {
    method: "POST",
    body: body as unknown as BodyInit,
  });
}

// ---------------- Labels (ECMS) ----------------

export function issueLabels(body: {
  order_ids?: string[];
  platforms?: string[];
  max_workers?: number;
  // momo 専用: 運送会社 + 送り状番号 (選択した momo 注文すべてに適用)
  momo_delivery?: { delivery_company: string; delivery_no: string };
} = {}): Promise<LabelIssuanceJobDto> {
  // 2026-06-09 非同期化: 即座に job_id を返す。フロントは getLabelJob で polling。
  return apiFetch<LabelIssuanceJobDto>("/api/labels/issue", {
    method: "POST",
    body: body as unknown as BodyInit,
  });
}

export function getLabelJob(jobId: string): Promise<LabelIssuanceJobDto> {
  return apiFetch<LabelIssuanceJobDto>(`/api/labels/jobs/${jobId}`);
}

// 2026-06-24 momo 新運用: 送り状番号「だけ」登録 (momo API は叩かない=Shipped 化しない)。
// 実出荷時 (出荷実績 CSV 取込トリガ) の非同期ジョブが Shipped 化 + ラベル取得を行う。
export function registerMomoTracking(body: {
  order_ids: string[];
  delivery_company: string;
  delivery_no: string;
}): Promise<{
  registered: number;
  delivery_company: string;
  delivery_no: string;
  message: string;
}> {
  return apiFetch("/api/labels/momo/register-tracking", {
    method: "POST",
    body: body as unknown as BodyInit,
  });
}

// ---------------- Air-Logi ----------------

export function downloadUploadCsv(body: {
  status?: string;
  one_row_per_item?: boolean;
  only_unexported?: boolean;
  mark_exported?: boolean;
  exported_today?: boolean;
  platforms?: string[];
  country_codes?: string[];
  exclude_country_codes?: string[];
  filename_label?: string;
} = {}): Promise<{ blob: Blob; filename: string | null }> {
  return apiFetchBlob("/api/airlogi/upload-csv", {
    method: "POST",
    body: body as unknown as BodyInit,
  });
}

export function importAllocated(file: File): Promise<AllocationImportResponse> {
  const fd = new FormData();
  fd.append("file", file);
  return apiFetch<AllocationImportResponse>("/api/airlogi/import-allocated", {
    method: "POST",
    body: fd,
  });
}

export function importOrderExport(
  file: File,
): Promise<OrderExportImportResponse> {
  const fd = new FormData();
  fd.append("file", file);
  return apiFetch<OrderExportImportResponse>(
    "/api/orders/import-order-export",
    { method: "POST", body: fd },
  );
}

// ---------------- 出荷チェック (エアロジ突合) ----------------

export interface ReconcileAlertEntry {
  order_no: string;
  platform?: string | null;
  country_code?: string;
  status?: string;
  airlogi_status?: string;
  system_status?: string;
  registered_at?: string;
  allocated_at?: string;
  packing_status?: string;
  shipping_status?: string;
  exported_at?: string | null;
  days_since_export?: number | null;
  days_waiting?: number | null;
  has_incoming?: boolean;
  note?: string;
  jans?: {
    jan: string;
    required?: number;
    stock: number | null;
    incoming_date?: string | null;
  }[];
}

export interface ReconcileResult {
  as_of: string;
  alert_total: number;
  inputs: {
    unallocated_rows: number;
    history_rows: number;
    stock_rows: number;
    incoming_rows: number;
    has_stock_data: boolean;
  };
  flow_today: Record<string, {
    fetched: number;
    issued: number;
    issue_pending: number;
    error: number;
    exported: number;
    allocated: number;
    printed: number;
    shipped: number;
  }>;
  alerts: {
    missing_in_airlogi: ReconcileAlertEntry[];
    allocation_problem: ReconcileAlertEntry[];
    stock_available_unallocated: ReconcileAlertEntry[];
    ghost_in_airlogi: ReconcileAlertEntry[];
    packing_pending_old: ReconcileAlertEntry[];
  };
  waiting: {
    stock_wait: ReconcileAlertEntry[];
    waiting_unknown: ReconcileAlertEntry[];
  };
  info: {
    packing_pending_today: number;
    system_awaiting_allocation: number;
  };
}

export function reconcileShippingCheck(body: {
  unallocated: unknown[];
  allocation_history: unknown[];
  stock?: unknown[];
  incoming?: unknown[];
}): Promise<ReconcileResult> {
  return apiFetch<ReconcileResult>("/api/shipping-check/reconcile", {
    method: "POST",
    body: body as unknown as BodyInit,
  });
}

// ---------------- 再印刷 (注文番号指定) ----------------

export function reprintByOrderNos(orderNos: string[]): Promise<{
  blob: Blob;
  filename: string | null;
  headers: Headers;
}> {
  return apiFetchBlob("/api/print-jobs/reprint", {
    method: "POST",
    body: { order_nos: orderNos } as unknown as BodyInit,
  });
}

// ---------------- Print Jobs ----------------

export function createPrintJob(body: {
  order_ids?: string[];
  print_method?: string;
} = {}): Promise<PrintJobDto> {
  return apiFetch<PrintJobDto>("/api/print-jobs", {
    method: "POST",
    body: body as unknown as BodyInit,
  });
}

export function listPrintJobs(limit = 50): Promise<{ print_jobs: PrintJobDto[]; count: number }> {
  return apiFetch(`/api/print-jobs?limit=${limit}`);
}

export function getPrintJob(id: string): Promise<PrintJobDto> {
  return apiFetch<PrintJobDto>(`/api/print-jobs/${id}`);
}

export function printJobPdfUrl(id: string): string {
  return `/api/print-jobs/${id}/pdf`;
}

export function executePrint(id: string): Promise<PrintJobDto> {
  return apiFetch<PrintJobDto>(`/api/print-jobs/${id}/print`, {
    method: "POST",
  });
}

// ---------------- Fulfillment ----------------

export function notifyShipment(body: {
  order_ids: string[];
  dry_run?: boolean;
  // 2026-06-12 拡張:
  // - skip_shopee_notify: プラットフォーム notify を呼ばず Wonect 側 status だけ
  //   SHIPPED 化 (在庫入荷後の物理発送時に使う、B ケース)。
  // - include_shopee_tw_unallocated: Shopee TW で status != SHIPPED の全 Order を
  //   ECMS 先行発番 → Shopee 側 shipped 化 (Wonect status は維持)。在庫切れ
  //   注文の Shopee 側キャンセル防止のため。
  skip_shopee_notify?: boolean;
  include_shopee_tw_unallocated?: boolean;
}): Promise<FulfillmentResponse> {
  return apiFetch<FulfillmentResponse>("/api/fulfillment/notify", {
    method: "POST",
    body: body as unknown as BodyInit,
  });
}

// ---------------- Dashboard ----------------

export function getDashboardSummary(): Promise<DashboardSummary> {
  return apiFetch<DashboardSummary>("/api/dashboard/summary");
}

// ---------------- Errors ----------------

export function listErrors(days = 7, limit = 100): Promise<ErrorsResponse> {
  return apiFetch<ErrorsResponse>(`/api/errors?days=${days}&limit=${limit}`);
}

export function resetOrderStatus(
  orderId: string,
  targetStatus = "new",
): Promise<ResetStatusResponse> {
  return apiFetch<ResetStatusResponse>(`/api/orders/${orderId}/reset-status`, {
    method: "POST",
    body: { target_status: targetStatus } as unknown as BodyInit,
  });
}

// ---------------- Recipient name review (2026-07-22) ----------------

export function approveRecipientName(
  orderId: string,
  issue = true,
): Promise<RecipientNameActionResponse> {
  return apiFetch<RecipientNameActionResponse>(
    `/api/orders/${orderId}/approve-recipient-name`,
    {
      method: "POST",
      body: { issue } as unknown as BodyInit,
    },
  );
}

export function correctRecipientName(
  orderId: string,
  recipientName: string,
  issue = true,
): Promise<RecipientNameActionResponse> {
  return apiFetch<RecipientNameActionResponse>(
    `/api/orders/${orderId}/correct-recipient-name`,
    {
      method: "POST",
      body: { recipient_name: recipientName, issue } as unknown as BodyInit,
    },
  );
}

export function reissueEcmsLabel(
  orderId: string,
  recipientName?: string,
  recipientPhone?: string,
): Promise<ReissueEcmsLabelResponse> {
  const body: Record<string, string> = {};
  if (recipientName) body.recipient_name = recipientName;
  if (recipientPhone) body.recipient_phone = recipientPhone;
  return apiFetch<ReissueEcmsLabelResponse>(
    `/api/orders/${orderId}/reissue-ecms-label`,
    {
      method: "POST",
      body: body as unknown as BodyInit,
    },
  );
}

export function shippingLabelPdfUrl(orderId: string): string {
  return `/api/orders/${orderId}/shipping-label.pdf`;
}

// ---------------- Health ----------------

export function healthCheck(): Promise<{ status: string; env: string }> {
  return apiFetch("/healthz");
}
